import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, X, Check, User as UserIcon } from 'lucide-react';

/**
 * UserMultiSelect
 * Reusable multi-select user picker.
 * - Searches users by NAME (case-insensitive). Email is not used as a search target.
 * - Displays selected users as removable chips above the search input.
 *
 * Props:
 *   users:       Array<{ id, name?, email }>
 *   selectedIds: string[]
 *   onChange:    (newIds: string[]) => void
 *   placeholder: string
 *   testId:      string (prefix for data-testid)
 *   emptyText:   string
 */
const UserMultiSelect = ({
  users = [],
  selectedIds = [],
  onChange,
  placeholder = 'Nach Name suchen…',
  testId = 'user-multi-select',
  emptyText = 'Keine Treffer',
}) => {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const wrapperRef = useRef(null);

  const displayName = (u) => (u.name && u.name.trim() ? u.name : u.email?.split('@')[0] || u.id);

  const selectedUsers = useMemo(
    () => selectedIds.map(id => users.find(u => u.id === id)).filter(Boolean),
    [selectedIds, users]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = users.filter(u => !selectedIds.includes(u.id));
    if (!q) return pool.slice(0, 20);
    return pool
      .filter(u => (u.name || '').toLowerCase().includes(q))
      .slice(0, 20);
  }, [query, users, selectedIds]);

  const toggle = (id) => {
    if (selectedIds.includes(id)) onChange(selectedIds.filter(x => x !== id));
    else onChange([...selectedIds, id]);
  };

  const remove = (id) => onChange(selectedIds.filter(x => x !== id));

  // Close dropdown on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <div ref={wrapperRef} className="relative" data-testid={testId}>
      {/* Selected chips */}
      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`${testId}-chips`}>
          {selectedUsers.map(u => (
            <Badge
              key={u.id}
              variant="secondary"
              className="pl-2 pr-1 py-0.5 flex items-center gap-1 text-xs"
              data-testid={`${testId}-chip-${u.id}`}
            >
              <UserIcon className="w-3 h-3 opacity-60" />
              <span>{displayName(u)}</span>
              <button
                type="button"
                className="ml-0.5 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 p-0.5"
                onClick={() => remove(u.id)}
                data-testid={`${testId}-remove-${u.id}`}
                aria-label={`Entfernen: ${displayName(u)}`}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <Input
          className="pl-8 h-9 text-sm"
          placeholder={placeholder}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          data-testid={`${testId}-search`}
        />
      </div>

      {/* Dropdown */}
      {focused && (
        <div
          className="absolute left-0 right-0 mt-1 z-40 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          data-testid={`${testId}-dropdown`}
        >
          {filtered.length === 0 ? (
            <p className="p-3 text-xs text-gray-400 text-center">{emptyText}</p>
          ) : (
            filtered.map(u => (
              <button
                key={u.id}
                type="button"
                onClick={() => toggle(u.id)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 text-sm"
                data-testid={`${testId}-option-${u.id}`}
              >
                <UserIcon className="w-4 h-4 text-gray-400" />
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium">{displayName(u)}</p>
                  {u.email && <p className="truncate text-xs text-gray-400">{u.email}</p>}
                </div>
                {selectedIds.includes(u.id) && <Check className="w-4 h-4 text-green-600" />}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default UserMultiSelect;
